'use client';

import { RegisterSchema } from '@/helpers/schemas';
import { RegisterFormType } from '@/helpers/types';
import { Formik, FormikHelpers } from 'formik';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import NextLink from 'next/link';

import TextInput from '@/components/input/TextInput';
import BaseButton from '@/components/button/BaseButton';
import SignupPhoneNumberInput from '@/components/input/SignupPhoneNumberInput';
import PasswordRequirements from '@/components/common/PasswordRequirements';
import { registerUser } from '@/api/register';
import logo from '../../../public/assets/images/logo.svg';
import Image from 'next/image';
import { fetchLogin } from '@/redux/thunk/authThunk';
import { AppDispatch } from '@/redux/store';
import { AppleAuthComponent } from './apple-auth';
import GoogleAuthComponent from './google-auth/google-auth.component';
import EyeToggle from '@/components/common/EyeToggle';
import {
  TERMS_CONDITIONS_URL,
  PRIVACY_POLICY_URL,
} from '@/constants/apiConstants';

export const Register = () => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const searchParams = useSearchParams();
  const supplier_referral = searchParams.get('invitation_token');
  const r_id = searchParams.get('r_id');

  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const initialValues: RegisterFormType = {
    firstName: '',
    lastName: '',
    supplier_referral: supplier_referral || null,
    email: '',
    phoneNumber: '',
    dial_code: null,
    iso_code: null,
    password: '',
    confirmpassword: '',
    r_id: '',
  };

  type LoginArgsType = {
    email: string;
    password: string;
  };

  const handleRegister = useCallback(
    async (
      values: RegisterFormType,
      { setErrors, setFieldError, resetForm }: FormikHelpers<RegisterFormType>,
    ) => {
      setIsLoading(true);

      try {
        // Fire Google Analytics event for login button click
        if (
          typeof window !== 'undefined' &&
          typeof window.gtag === 'function'
        ) {
          window.gtag('event', 'web_register_clicked', {
            method: 'email',
          });
        }

        // Remove dial code from the start of the phone number if present
        let cleanPhoneNumber = values.phoneNumber;
        if (values.dial_code && cleanPhoneNumber.startsWith(values.dial_code)) {
          cleanPhoneNumber = cleanPhoneNumber.slice(values.dial_code.length);
        }

        const registrationData = {
          ...values,
          supplier_referral: supplier_referral || null,
          phoneNumber: cleanPhoneNumber,
          ...(r_id && { r_id }),
        };

        const responseData = await registerUser(registrationData);

        toast.success(responseData?.message);

        resetForm();

        const loginData: LoginArgsType = {
          email: values.email,
          password: values.password,
        };

        await dispatch(fetchLogin(loginData)).unwrap();
        // // Dispatch the login action
        // const loginData = responseData?.data;
        // dispatch(login(loginData));

        if (
          typeof window !== 'undefined' &&
          typeof window.gtag === 'function'
        ) {
          window.gtag('event', 'web_register_success', {
            method: 'email',
          });
        }

        router.replace('/');
      } catch (error: any) {
        if (error) {
          const validationErrors = error?.response?.data?.messages || {};
          if (
            typeof window !== 'undefined' &&
            typeof window.gtag === 'function'
          ) {
            window.gtag('event', 'web_register_error', {
              method: 'email',
            });
          }

          Object.keys(validationErrors).forEach((field) => {
            setFieldError(field, validationErrors[field][0]);
          });
        } else {
          // Handle other errors (e.g., network errors)
          console.error('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, router],
  );

  return (
    <div className=" pr-4">
      <div className="flex flex-col justify-start items-start w-full">
        <Image
          src={logo}
          alt="logo"
          width={250}
          priority
          className="cursor-pointer pt-4 3xl:pt-8 md:pt-0"
        />

        <h6 className="pt-4 3xl:pt-10">Sign Up</h6>
        <h4 className="text-body-md pt-3 3xl:pt-7">
          Join us and start enjoying all the benefits ZYNOFF has to offer. Fill
          out the below form to get started:
        </h4>

        <Formik
          initialValues={initialValues}
          validationSchema={RegisterSchema}
          onSubmit={handleRegister}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <div className="w-full">
              <div className="flex flex-col md:flex-row gap-4 mt-4 3xl:mt-8">
                <div className="w-full">
                  <TextInput
                    variant="bordered"
                    labelPlacement="outside"
                    label="First Name"
                    name="firstName"
                    placeholder="John"
                    value={values.firstName}
                    size="md"
                    isInvalid={!!errors.firstName && !!touched.firstName}
                    errorMessage={errors.firstName}
                    onChange={handleChange}
                    isDisabled={isLoading}
                    className="font-bold"
                  />
                </div>

                <div className="w-full">
                  <TextInput
                    variant="bordered"
                    labelPlacement="outside"
                    label="Last Name"
                    name="lastName"
                    placeholder="Doe"
                    value={values.lastName}
                    size="md"
                    isInvalid={!!errors.lastName && !!touched.lastName}
                    errorMessage={errors.lastName}
                    onChange={handleChange}
                    isDisabled={isLoading}
                    className="font-bold"
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 mt-6">
                <div className="w-full">
                  <TextInput
                    variant="bordered"
                    labelPlacement="outside"
                    label="Email"
                    type="email"
                    name="email"
                    placeholder="john@zynoff.co.nz"
                    value={values.email}
                    size="md"
                    isInvalid={!!errors.email && !!touched.email}
                    errorMessage={errors.email}
                    onChange={handleChange}
                    isDisabled={isLoading}
                    className="font-bold"
                  />
                </div>

                <div className="w-full">
                  <SignupPhoneNumberInput
                    id="phoneNumber"
                    variant="bordered"
                    labelPlacement="outside"
                    label="Phone Number"
                    name="phoneNumber"
                    placeholder="0221234567"
                    value={values.phoneNumber}
                    size="md"
                    isRequired={true}
                    isInvalid={!!errors.phoneNumber && !!touched.phoneNumber}
                    errorMessage={errors.phoneNumber}
                    onChange={(cleanNumber, data) => {
                      // Set the clean 11-digit number and country data to Formik
                      setFieldValue('phoneNumber', cleanNumber);
                      setFieldValue('dial_code', data?.dialCode || null);
                      setFieldValue('iso_code', data?.countryCode || null);
                    }}
                    disabled={isLoading}
                    customClassNames={{ label: 'font-bold' }}
                  />
                </div>
              </div>

              <div className="w-full pt-6">
                <TextInput
                  variant="bordered"
                  labelPlacement="outside"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="************"
                  value={values.password}
                  size="md"
                  isInvalid={!!errors.password && !!touched.password}
                  errorMessage={errors.password}
                  onChange={handleChange}
                  isDisabled={isLoading}
                  className="font-bold"
                  endContent={
                    <EyeToggle
                      isVisible={showPassword}
                      onClick={() => setShowPassword((prev) => !prev)}
                      disabled={!values.password}
                    />
                  }
                />
              </div>

              <div className="w-full mt-1">
                <PasswordRequirements />
              </div>

              <div className="w-full pt-6">
                <TextInput
                  variant="bordered"
                  labelPlacement="outside"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmpassword"
                  placeholder="************"
                  value={values.confirmpassword}
                  size="md"
                  isInvalid={
                    !!errors.confirmpassword && !!touched.confirmpassword
                  }
                  errorMessage={errors.confirmpassword}
                  onChange={handleChange}
                  isDisabled={isLoading}
                  className="font-bold"
                  endContent={
                    <EyeToggle
                      isVisible={showConfirmPassword}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      disabled={!values.confirmpassword}
                    />
                  }
                />
              </div>

              <div className="text-body-md pt-4 3xl:pt-8">
                {/* <Checkbox
                size="sm"
                isDisabled={isLoading}
                isSelected={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              /> */}
                By signing up, you acknowledge that you understand and agree to
                our{' '}
                <a
                  href={TERMS_CONDITIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-light-linkColor-primary"
                >
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-light-linkColor-primary"
                >
                  Privacy Policy
                </a>
              </div>

              <div className="w-full pt-4 3xl:pt-8">
                <BaseButton
                  // isDisabled={!isChecked}
                  color="primary"
                  width="100%"
                  size="md"
                  radius="sm"
                  onClick={() => handleSubmit()}
                  isLoading={isLoading}
                >
                  Create Account
                </BaseButton>
              </div>
              <div className="w-full my-[30px] flex items-center justify-center">
                <span className="text-[#6D788D] text-base font-normal">
                  or continue with
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <GoogleAuthComponent supplier_referral={supplier_referral} />
                <AppleAuthComponent supplier_referral={supplier_referral} />
              </div>
              <div className="w-full pt-4 3xl:pt-8 text-center">
                <span style={{ cursor: 'pointer' }}>
                  Already have an account?
                </span>
                <NextLink
                  style={{ cursor: 'pointer' }}
                  className="text-primary pl-1"
                  href={r_id ? `/login?r_id=${r_id}` : '/login'}
                >
                  Sign in
                </NextLink>
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};
